import { Box, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment-timezone';

import { LocalCampaignFlight } from './useCampaignFlights';
import { getFlightStatusBadge } from '../../../utils/FlightStatusUtils';
import { CDCurrencyField } from '../../../components/CDCurrencyField';
import { CDTextField } from '../../../components/CDTextField';
import Utils from '../../../components/Utils';
import CDDateTimePicker from '../../../components/CDDateTimePicker';
import { CampaignFlight } from '../../../types/Campaign';

interface FlightTableRowProps {
    flight: LocalCampaignFlight;
    displayIndex: number;
    isEditing: boolean;
    isLive: boolean;
    isUpcoming: boolean;
    isExistingCampaign: boolean;
    isAutoAdded?: boolean;
    showImpressionGoal?: boolean;
    impressionMode?: boolean;
    onImpressionGoalChange?: (flightKey: string | number, impressionGoal: number | null) => void;
    onBudgetChange?: (flightKey: string | number, budgetAmount: number) => void;
    getFlightMinStartDate: (flightKey: string | number) => Date;
    getFlightMaxEndDate: (flightKey: string | number, start: Date | null) => Date | undefined;
    isFlightValid: (flight: LocalCampaignFlight) => boolean;
    onFlightChange: (flightKey: string | number, field: keyof CampaignFlight, value: any) => void;
    onSave: (flightKey: string | number) => void;
    onEdit: (flightKey: string | number) => void;
    onDelete: (flightKey: string | number) => void;
}

export default function FlightTableRow({
    flight,
    displayIndex,
    isEditing,
    isLive,
    isUpcoming,
    isExistingCampaign,
    isAutoAdded,
    showImpressionGoal = false,
    impressionMode = false,
    onImpressionGoalChange,
    onBudgetChange,
    getFlightMinStartDate,
    getFlightMaxEndDate,
    isFlightValid,
    onFlightChange,
    onSave,
    onEdit,
    onDelete,
}: FlightTableRowProps) {
    const flightKey = flight.id !== undefined ? flight.id : flight.tempId!;
    const isLiveLocked = isLive && isExistingCampaign && !isAutoAdded;
    const isExistingFlight = flight.id !== undefined;
    const remaining = flight.budgetAmount - flight.totalCost;
    const isBudgetBelowSpent = isLive && flight.budgetAmount < (flight.totalCost ?? 0);

    // Editable: live (budget only) OR upcoming (budget + dates) OR new flight
    const isEditAllowed = !isExistingFlight || isLive || isUpcoming;

    return (
        <TableRow key={flightKey}>
            <TableCell>{displayIndex}</TableCell>

            <TableCell>{getFlightStatusBadge(flight.startDateTime, flight.endDateTime)}</TableCell>

            {/* Budget */}
            <TableCell>
                {isEditing ? (
                    <>
                        <CDCurrencyField
                            variant="outlined"
                            size="small"
                            value={flight.budgetAmount || ''}
                            onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                if (onBudgetChange) {
                                    onBudgetChange(flightKey, val);
                                } else {
                                    onFlightChange(flightKey, 'budgetAmount', val);
                                }
                            }}
                            sx={{ width: 120 }}
                            error={
                                (isLive || isUpcoming) &&
                                (!flight.budgetAmount || flight.budgetAmount <= 0 || isBudgetBelowSpent)
                            }
                            required={isLive || isUpcoming}
                            disabled={!isLive && isLiveLocked}
                        />
                        {isBudgetBelowSpent && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                Cannot be less than spent ({Utils.formatCurrency(flight.totalCost)})
                            </Typography>
                        )}
                    </>
                ) : (
                    <Typography>{flight.budgetAmount ? Utils.formatCurrency(flight.budgetAmount) : '-'}</Typography>
                )}
            </TableCell>

            {/* Spent */}
            <TableCell>{flight.totalCost ? Utils.formatCurrency(flight.totalCost) : '-'}</TableCell>

            {/* Remaining */}
            <TableCell>{remaining ? Utils.formatCurrency(remaining) : '-'}</TableCell>

            {/* Impression Goal (per-flight) — COGPLAT-2728 / COGPLAT-2794 */}
            {showImpressionGoal && (
                <TableCell>
                    {impressionMode && isEditing ? (
                        <CDTextField
                            type="number"
                            variant="outlined"
                            size="small"
                            required
                            value={flight.impressionGoal ?? ''}
                            onChange={(e) => {
                                const raw = e.target.value === '' ? null : parseInt(e.target.value, 10) || 0;
                                const val = raw != null ? Math.min(Math.max(raw, 0), 2147483647) : null;
                                if (onImpressionGoalChange) {
                                    onImpressionGoalChange(flightKey, val);
                                } else {
                                    onFlightChange(flightKey, 'impressionGoal', val);
                                }
                            }}
                            inputProps={{ min: 0, max: 2147483647 }}
                            sx={{ width: 140 }}
                            disabled={!isLive && isLiveLocked}
                            error={impressionMode && (!flight.impressionGoal || flight.impressionGoal <= 0)}
                        />
                    ) : (
                        <Typography>
                            {impressionMode && flight.impressionGoal != null && flight.impressionGoal > 0
                                ? flight.impressionGoal.toLocaleString()
                                : '-'}
                        </Typography>
                    )}
                </TableCell>
            )}

            {/* Start Date */}
            <TableCell>
                {isEditing ? (
                    <CDDateTimePicker
                        disablePast
                        value={
                            flight.startDateTime ? moment.tz(flight.startDateTime, 'America/New_York').toDate() : null
                        }
                        onChange={(dateValue: Date | null) => onFlightChange(flightKey, 'startDateTime', dateValue)}
                        showError={(isLive || isUpcoming) && !flight.startDateTime}
                        label="Start Date"
                        disabled={isLiveLocked}
                        minDate={getFlightMinStartDate(flightKey)}
                    />
                ) : (
                    <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.3 }}>
                            {flight.startDateTime
                                ? moment.tz(flight.startDateTime, 'America/New_York').format('M/D/YY')
                                : '-'}
                        </Typography>
                        {flight.startDateTime && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {moment.tz(flight.startDateTime, 'America/New_York').format('h:mm A')} ET
                            </Typography>
                        )}
                    </Box>
                )}
            </TableCell>

            {/* End Date */}
            <TableCell>
                {isEditing ? (
                    <CDDateTimePicker
                        disablePast
                        value={flight.endDateTime ? moment.tz(flight.endDateTime, 'America/New_York').toDate() : null}
                        onChange={(dateValue: Date | null) => onFlightChange(flightKey, 'endDateTime', dateValue)}
                        showError={(isLive || isUpcoming) && !flight.endDateTime}
                        label="End Date"
                        minDate={
                            flight.startDateTime
                                ? moment.tz(flight.startDateTime, 'America/New_York').toDate()
                                : undefined
                        }
                        maxDate={getFlightMaxEndDate(flightKey, flight.startDateTime)}
                        disabled={isLiveLocked}
                    />
                ) : (
                    <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.3 }}>
                            {flight.endDateTime
                                ? moment.tz(flight.endDateTime, 'America/New_York').format('M/D/YY')
                                : '-'}
                        </Typography>
                        {flight.endDateTime && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {moment.tz(flight.endDateTime, 'America/New_York').format('h:mm A')} ET
                            </Typography>
                        )}
                    </Box>
                )}
            </TableCell>

            {/* Actions */}
            <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {isEditing ? (
                        <>
                            {/* Save */}
                            <Tooltip
                                title={
                                    !isFlightValid(flight)
                                        ? isLive && isBudgetBelowSpent
                                            ? `Budget cannot be less than spent (${Utils.formatCurrency(
                                                  flight.totalCost
                                              )})`
                                            : 'Please fill in all required fields'
                                        : 'Save flight'
                                }
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => onSave(flightKey)}
                                        disabled={!isFlightValid(flight)}
                                    >
                                        <CheckIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            {/* Delete — blocked for all existing flights */}
                            <Tooltip
                                title={
                                    isLive
                                        ? 'Cannot delete a live flight'
                                        : isExistingFlight
                                        ? 'Existing flights cannot be deleted'
                                        : 'Delete flight'
                                }
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => onDelete(flightKey)}
                                        disabled={isLive || isExistingFlight}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    ) : (
                        <>
                            {/* Edit */}
                            <Tooltip
                                title={
                                    !isEditAllowed
                                        ? 'Existing flights cannot be edited'
                                        : isLive
                                        ? 'Edit budget'
                                        : 'Edit flight'
                                }
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => onEdit(flightKey)}
                                        disabled={!isEditAllowed}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            {/* Delete */}
                            <Tooltip
                                title={
                                    isLive
                                        ? 'Cannot delete a live flight'
                                        : isExistingFlight
                                        ? 'Existing flights cannot be deleted'
                                        : 'Delete flight'
                                }
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => onDelete(flightKey)}
                                        disabled={isLive || isExistingFlight}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    )}
                </Box>
            </TableCell>
        </TableRow>
    );
}
